/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import classNames from 'classnames';
import $ from 'jquery';
import PropTypes from 'prop-types';
import Q from 'q';
import type { LegacyContextType } from 'types/legacy-context-types';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import isMobileApp from 'js/lib/isMobileApp';

import type ItemMetadata from 'pages/open-course/common/models/itemMetadata';

import { Button, Grid, Typography2, VisuallyHidden, breakpoints } from '@coursera/cds-core';
import { CloseIcon, MaximizeIcon } from '@coursera/cds-icons';

import Icon from 'bundles/iconfont/Icon';
import {
  connectToWidget,
  registerWidget,
  removeWidget,
  toggleWidgetError,
} from 'bundles/widget/actions/WidgetManagerActions';
import WidgetErrorMessage from 'bundles/widget/components/WidgetErrorMessage';
import { styles as headerStyles } from 'bundles/widget/components/cds/WidgetInPlaceModalHeader';
import ItemCompleteButton from 'bundles/widget/components/itemCompleteButton/ItemCompleteButton';
import { FocusableElements, RPCActions, WidgetReadyStates } from 'bundles/widget/constants/WidgetConstants';
import type { ScormConfig } from 'bundles/widget/lib/scorm/scorm-connect/types/courseraApi';
import type {
  ConnectedWidget,
  WidgetError,
  store as WidgetManagerStoreType,
  WidgetRegistry,
  WidgetState,
} from 'bundles/widget/reducers/WidgetManagerReducer';
import type { CourseraConnectMessage } from 'bundles/widget/types/Request';
import type { WidgetSession, WidgetSessionConfiguration, WidgetSessionId } from 'bundles/widget/types/WidgetSession';
import { isRpcAction } from 'bundles/widget/types/WidgetSession';
import { handleWidgetRpcAction } from 'bundles/widget/utils';

import _t from 'i18n!nls/widget';

import 'css!bundles/widget/components/__styles__/WidgetFrame';

const styles = {
  controlsRow: css`
    margin-top: var(--cds-spacing-400);
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: var(--cds-spacing-400);

    ${breakpoints.down('xs')} {
      flex-direction: column;
      align-items: flex-start;
      gap: 0;
    }
  `,
  markAsComplete: css`
    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-300);
    }
  `,
  completedButtonOnly: css`
    ${breakpoints.down('xs')} {
      margin-top: 0;
    }
  `,
};

type PropsFromStore = {
  widgetRegistry: WidgetRegistry;
  widgetState?: WidgetState;
  error?: WidgetError;
};

export type PropsFromCaller = {
  onReceiveMessage?: (
    request: CourseraConnectMessage
  ) => Q.Promise<string | undefined | void> | Promise<string | undefined | void>;
  session: WidgetSession;
  sessionId?: WidgetSessionId;
  inWidgetItem?: boolean;
  isDisabled?: boolean;
  // NOTE: isComplete and markComplete should only be true when inWidgetItem=true
  isComplete?: boolean;
  markComplete?: () => void;
  widgetId: string;
  parentRpcActions?: Array<string>;
  fallbackDefaultHeight?: string;
  showPopupButton?: boolean;
  widgetContentTitle?: string;
  onToggleExpanded?: () => void;
  nextItem?: ItemMetadata;
};

type PropsToComponent = PropsFromStore & PropsFromCaller;

type Stores = {
  WidgetManagerStore: InstanceType<typeof WidgetManagerStoreType>;
};

type State = {
  specifiedHeight: string;
  expanded: boolean;
};

type onReceiveMessageResponse = WidgetSessionConfiguration | string | undefined | void | any;

const KEY_TAB = 9;

export class LegacyWidgetFrame extends React.Component<PropsToComponent, State> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
    getStore: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof LegacyWidgetFrame.contextTypes>;

  static defaultProps = {
    onReceiveMessage: () => {
      return Q();
    },
    inWidgetItem: false,
    parentRpcActions: [],
    fallbackDefaultHeight: '500px',
    showPopupButton: true,
  };

  declare registeredWidget: boolean | undefined;

  declare attemptedConnection: boolean | undefined;

  state: State = {
    specifiedHeight: '',
    expanded: false,
  };

  closeButton: HTMLButtonElement | null = null;

  maximizeButton: HTMLButtonElement | null = null;

  secretButton: HTMLButtonElement | null = null;

  widgetFrame: HTMLDivElement | null = null;

  iframeElement: HTMLIFrameElement | null = null;

  componentDidMount() {
    this.refreshWidgetState();
    if (this.iframeElement && this.iframeElement instanceof HTMLIFrameElement) {
      // set as custom attribute since react 15 automatically removes the allow attributes in iframes
      // https://github.com/facebook/react/issues/12225
      this.iframeElement.setAttribute('allow', 'camera');
      this.iframeElement.focus();
    }

    document.addEventListener('keydown', this.cycleTabs, false);
  }

  componentDidUpdate() {
    this.refreshWidgetState();
  }

  componentWillUnmount() {
    const { widgetState, widgetRegistry, widgetId } = this.props;

    if (!widgetState) {
      return;
    }

    const widgetRegistration = widgetRegistry[widgetId];

    if (widgetId && widgetRegistration && (widgetRegistration as ConnectedWidget)?.removeListenerHandle) {
      this.context.executeAction(removeWidget, {
        widgetId: this.props.widgetId,
        removeListenerHandle: (widgetRegistration as ConnectedWidget)?.removeListenerHandle,
      });
    }

    document.removeEventListener('keydown', this.cycleTabs, false);
  }

  onExpandToggle = () => {
    // TODO @sgogia
    // slightly hacky way of ensuring body (higher in hierarchy) can't be scrolled when widget expanded
    // document body checks are only required for getting rid of flow issues (body should be defined for this event)
    const { expanded } = this.state;
    const { onToggleExpanded } = this.props;

    if (!expanded) {
      $('body').addClass(' noscroll');
    } else {
      $('body').removeClass(' noscroll');
    }

    if (onToggleExpanded) {
      onToggleExpanded();
    }

    this.setState({ expanded: !expanded }, () => {
      if (!expanded && this.closeButton) {
        this.closeButton.focus();
      } else if (expanded && this.maximizeButton) {
        this.maximizeButton.focus();
      }
    });
  };

  onReceiveMessage = (
    request: CourseraConnectMessage
  ): Q.Promise<onReceiveMessageResponse> | Promise<onReceiveMessageResponse> | Function | undefined => {
    const {
      session: { rpcActionTypes, configuration },
      sessionId,
      widgetId,
      parentRpcActions,
    } = this.props;
    if (request.type === RPCActions.LOAD_WIDGET_ERROR) {
      const { body } = request;
      if (body) {
        const { error } = body;
        this.context.executeAction(toggleWidgetError, { widgetId, error });
      }
      return Q();
    }
    if (rpcActionTypes?.length) {
      if (isRpcAction(request.type) && rpcActionTypes.includes(request.type)) {
        if (request.type === RPCActions.GET_SESSION_CONFIGURATION) {
          return Q(configuration);
        } else if (request.type === RPCActions.SET_COMPLETE) {
          this.props.markComplete?.();
          return this.props.onReceiveMessage?.(request);
        } else if (request.type === RPCActions.SET_HEIGHT) {
          const { body } = request;
          if (body) {
            const specifiedHeight = body.height;
            if (specifiedHeight) {
              this.setState({ specifiedHeight });
            }
            return Q('Height set');
          } else {
            return Q('Failed to set height - empty request');
          }
        } else if (parentRpcActions && parentRpcActions.includes(request.type) && this.props.onReceiveMessage) {
          return this.props.onReceiveMessage(request);
        } else if (sessionId) {
          return handleWidgetRpcAction(request, sessionId);
        }
      } else {
        // Should never really happen
        return Q.reject(`Unknown or unauthorized request type: ${request.type}`);
      }
    }

    // redundantly returning undefined here to fix ts warning
    return undefined;
  };

  getFocusableElements = () => {
    if (this.widgetFrame) {
      const allFocusableElements = [
        ...this.widgetFrame.querySelectorAll<HTMLElement>(FocusableElements.FOCUSABLE_SELECTOR),
      ];
      const focusableElementsWithoutDisabledButtons = allFocusableElements.filter((el) => !el.hasAttribute('disabled'));

      return focusableElementsWithoutDisabledButtons;
    }

    return [];
  };

  isOnMobile = () => {
    const applicationStore = this.context.getStore('ApplicationStore');
    const userAgent = applicationStore?.getUserAgent();
    const isMobileBrowser = userAgent && userAgent.isMobileBrowser;
    return isMobileApp.get() || isMobileBrowser;
  };

  refreshWidgetState = () => {
    const { widgetState, widgetId } = this.props;

    if (!widgetState || widgetState.readyState === WidgetReadyStates.NOT_LOADED) {
      this.registerWidget();
    } else if (widgetId && widgetState.readyState === WidgetReadyStates.REGISTERING) {
      this.connectToWidget();
    }
  };

  registerWidget() {
    const { widgetId, widgetRegistry } = this.props;

    if (this.registeredWidget) {
      return;
    }

    this.registeredWidget = true;
    this.context.executeAction(registerWidget, {
      widgetId,
      widgetRegistry,
    });
  }

  redirectTabFocus = (e: KeyboardEvent, focusableElement: $TSFixMe) => {
    e.preventDefault();
    focusableElement.focus();
  };

  showExpansionButton = () => {
    const { showPopupButton } = this.props;
    const { expanded } = this.state;

    return !expanded && !this.isOnMobile() && showPopupButton;
  };

  showCompleteButton = () => {
    const { session, inWidgetItem } = this.props;
    const { expanded } = this.state;
    // If this is a SCORM plugin, we do not know ahead of time if the SCO will mark itself as complete so, although we
    // have the SET_COMPLETE RPC action, it might never be called. In this case, we let the configuration option set by the author
    // determine whether or not to show the completion button
    const isScorm = !!(session?.configuration as ScormConfig)?.SCORM_entry;
    const hideCompleteButtonForScorm = isScorm && (session?.configuration as ScormConfig)?.hideCompleteButton;
    const hasCompletedRPCAction =
      !isScorm && session?.rpcActionTypes && session.rpcActionTypes.includes(RPCActions.SET_COMPLETE);
    const showCompletionButton = !expanded && inWidgetItem && !hasCompletedRPCAction && !hideCompleteButtonForScorm;
    return showCompletionButton;
  };

  cycleTabs = (event: KeyboardEvent) => {
    const { expanded } = this.state;

    if (!expanded || event.keyCode !== KEY_TAB) {
      return;
    }

    // grab it every time in case the modal is updated and there's new contents in there
    const focusableElements = this.getFocusableElements();

    const lastFocusableElement = focusableElements[focusableElements.length - 1] as HTMLButtonElement;
    const firstFocusableElement = focusableElements[0] as HTMLButtonElement;
    const currentElement = document.activeElement;

    const showExpansionButton = this.showExpansionButton();
    const showCompletionButton = this.showCompleteButton();

    if (event.shiftKey) {
      // SHIFT + TAB
      if (currentElement === firstFocusableElement) {
        const isCompletionButtonHidden = !showExpansionButton && !showCompletionButton;
        /* tab to iframe if completion button is hidden */
        this.redirectTabFocus(event, isCompletionButtonHidden ? this.iframeElement : lastFocusableElement);
      }
    } else if (currentElement === lastFocusableElement) {
      // TAB
      this.redirectTabFocus(event, firstFocusableElement);
    }
  };

  tabToCloseButton = () => {
    if (this.closeButton) {
      this.closeButton.focus();
    }
  };

  connectToWidget() {
    const { widgetId, widgetRegistry, session } = this.props;

    if (this.attemptedConnection) {
      return;
    }

    this.attemptedConnection = true;
    const widgetRegistration = widgetRegistry[widgetId];
    const { rpcActionTypes } = session;

    if (widgetRegistration && this.iframeElement) {
      // Since DOM elements can't be passed through actionContext, send wrapper around `postMessage`.
      const sendMessageToIFrame = (message: $TSFixMe, src: $TSFixMe) => {
        if (this.iframeElement && this.iframeElement.contentWindow) {
          this.iframeElement.contentWindow.postMessage(message, src);
        }
      };

      this.context.executeAction(connectToWidget, {
        iFrameSrc: this.iframeElement.src || '',
        widgetId,
        rpcActionTypes,
        // TODO: type these callbacks to match
        onReceiveMessage: this.onReceiveMessage,
        sendMessageToIFrame,
      });
    }
  }

  trySmartHeightDefault = () => {
    const iframe = this.iframeElement;
    if (!!iframe && !this.state.specifiedHeight) {
      try {
        const contentsHeight = iframe?.contentWindow?.document.body.scrollHeight;
        if (contentsHeight && contentsHeight > 0) {
          iframe.height = contentsHeight + 'px';
        }
      } catch {
        // if we can't get the height of the contents, we'll keep our default height
      }
    }
  };

  renderSpinner(width: string, height: string | undefined) {
    return (
      <div
        className="horizontal-box align-items-absolute-center widget-loader"
        style={{ width, height }}
        data-testid="widget-spinner"
      >
        <Icon name="spinner" size="2x" spin={true} className="color-hint-text" />
      </div>
    );
  }

  render() {
    const { session, widgetState, error, fallbackDefaultHeight, widgetId, widgetContentTitle, nextItem } = this.props;
    const { src, sandbox } = session;
    const { expanded, specifiedHeight } = this.state;
    const height = specifiedHeight || fallbackDefaultHeight;
    const width = '100%';

    // note isComplete and markComplete should only exist if inWidgetItem true
    const { isComplete, markComplete } = this.props;

    if (!widgetState) {
      return this.renderSpinner(width, height);
    }

    const { readyState } = widgetState;
    const isWidgetLoading = readyState === WidgetReadyStates.REGISTERING || readyState === WidgetReadyStates.NOT_LOADED;

    // Because reference to iFrame is needed, hide iFrame via CSS instead of not rendering it.
    const iframeClassName = classNames('widget-iframe', {
      hidden: !!error,
      'default-height': !specifiedHeight,
    });

    // TODO: separate renderExpandedView into its own function
    const expandedClass = expanded ? 'expanded' : 'unexpanded';
    const showExpansionButton = this.showExpansionButton();
    const showCompletionButton = this.showCompleteButton();

    // if the widget frame is expanded we should give it modal semantics
    const dialogProperties = expanded ? { 'aria-modal': true, role: 'dialog' } : {};

    return (
      <div
        ref={(node) => {
          this.widgetFrame = node;
        }}
        className={`rc-WidgetFrame ${expandedClass} legacyWidgetFrame`}
        {...dialogProperties}
      >
        {expanded && <div className="dimmer" />}
        <div className="widget-container vertical-box">
          {expanded && (
            <Grid
              data-testid="widget-modal"
              css={headerStyles.modalHeader}
              justifyContent={widgetContentTitle ? 'space-between' : 'flex-end'}
              container
            >
              <Typography2 component="h1" variant="titleSmall" css={headerStyles.truncated}>
                {widgetContentTitle}
              </Typography2>

              <Button
                type="button"
                aria-label={_t('#{widgetContentTitle} window. Close window', { widgetContentTitle })}
                onClick={this.onExpandToggle}
                icon={<CloseIcon size="small" />}
                iconPosition="after"
                variant="ghost"
                size="small"
                className="read-only-wrapper-allow-interaction"
                ref={(node) => {
                  this.closeButton = node;
                }}
              >
                {_t('Close')}
              </Button>
            </Grid>
          )}
          <div className="frame-container">
            {error && <WidgetErrorMessage />}
            {isWidgetLoading && this.renderSpinner(width, height)}

            <iframe
              title={session.iframeTitle}
              src={src}
              id={widgetId}
              height={height}
              className={iframeClassName}
              width={width}
              sandbox={sandbox}
              ref={(frame) => {
                this.iframeElement = frame;
              }}
              onLoad={this.trySmartHeightDefault}
              data-testid="widget-iframe"
            >
              {_t('Iframes are not supported in your browser.')}
            </iframe>
          </div>

          {showExpansionButton && (
            <div className="widget-controls" css={styles.controlsRow}>
              <Button
                ref={(node) => {
                  this.maximizeButton = node;
                }}
                onClick={this.onExpandToggle}
                aria-label={_t('Expand #{widgetContentTitle} window', { widgetContentTitle })}
                variant="secondary"
                iconPosition="before"
                size="medium"
                icon={<MaximizeIcon />}
              >
                {_t('Expand')}
              </Button>
              {showCompletionButton && typeof isComplete === 'boolean' && !!markComplete && (
                <div css={isComplete ? {} : styles.markAsComplete}>
                  <ItemCompleteButton isComplete={isComplete} markComplete={markComplete} nextItem={nextItem} />
                </div>
              )}
            </div>
          )}
          {/* Helps us tab back to the close button if the complete button does not exist.
            This is because an iframe is used to display contents */}
          {!showExpansionButton && !showCompletionButton && (
            <VisuallyHidden component="span" className="rc-A11yScreenReaderOnly">
              <button
                aria-hidden={expanded}
                type="button"
                className="secret-button"
                ref={(node) => {
                  this.secretButton = node;
                }}
                onFocus={this.tabToCloseButton}
              >
                {_t('Go to Close')}
              </button>
            </VisuallyHidden>
          )}
          {!showExpansionButton && showCompletionButton && typeof isComplete === 'boolean' && !!markComplete && (
            <div
              className="widget-controls"
              aria-hidden={expanded}
              css={[styles.controlsRow, isComplete ? styles.completedButtonOnly : {}]}
            >
              <ItemCompleteButton isComplete={isComplete} markComplete={markComplete} />
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default connectToStores<PropsToComponent, PropsFromCaller, Stores>(
  ['WidgetManagerStore'],
  ({ WidgetManagerStore }, props) => {
    const widgetManagerStoreState = WidgetManagerStore.getState();
    const { widgetId } = props;
    const widgetState = widgetManagerStoreState.widgetStates[widgetId];
    const error = widgetState && widgetState.error;

    return {
      widgetRegistry: widgetManagerStoreState.widgetRegistry,
      widgetState,
      error,
    };
  }
)(LegacyWidgetFrame);
